import {
    Box,
    Button,
    Card,
    Divider,
    IconButton,
    Skeleton,
    Stack,
    Tab,
    Tabs,
    Tooltip,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material'
import { FC, useEffect, useMemo, useState } from 'react'
import useInfiniteScroll from 'react-infinite-scroll-hook'
import {
    useReadAllApiV1NotificationsFeedUserEventReadAllPatch,
    UserNotification,
} from '../../../api/gen/base'
import { useInfiniteNotificationsFeedUserEventListQuery } from '../../../api/hooks/useInfiniteNotificationsFeedUserEventListQuery'
import { useInvalidateNotificationsFeedUserEventListQuery } from '../../../api/hooks/useInvalidateNotificationsFeedUserEventListQuery'
import { useInvalidateNotificationsFeedUserEventTotal } from '../../../api/hooks/useInvalidateNotificationsFeedUserEventTotal'
import BellIcon from '../../../assets/icons/Bell.svg?react'
import GearIcon from '../../../assets/icons/Gear.svg'
import { NotificationListItem } from '../../../components/NotificationListItem'
import { APP_TOP_INDENT, APP_TOP_INDENT_MOBILE } from '../../../constants/app.indents'
import { useNotificationsStore } from '../../../store/storeNotifications'
import { useWrapperStore } from '../../../store/storeWrapper'
import { SidebarType } from '../../../types/sidebars'

export const NotificationsPage: FC = () => {
    const { breakpoints } = useTheme()

    const [tab, setTab] = useState(0)

    const isMobile = useMediaQuery(breakpoints.down('md'))

    const { setOpenSidebarType, openSidebarType } = useWrapperStore((state) => state)
    const { numberOfUnreadNotifications } = useNotificationsStore((state) => state)

    const invalidateNotificationsListQueries = useInvalidateNotificationsFeedUserEventListQuery()
    const invalidateNotificationsTotalQueries = useInvalidateNotificationsFeedUserEventTotal()

    const {
        data,
        isFetching,
        hasNextPage,
        fetchNextPage,
        isFetchedAfterMount,
        isFetchingNextPage,
    } = useInfiniteNotificationsFeedUserEventListQuery({
        is_read: tab === 0 ? undefined : tab !== 1,
        sort_by: 'DESC',
    })

    const {
        isSuccess: isSuccessReadAll,
        mutate: mutateReadAll,
        isPending: isPendingReadAll,
    } = useReadAllApiV1NotificationsFeedUserEventReadAllPatch()

    const [infiniteRef] = useInfiniteScroll({
        loading: isFetching,
        hasNextPage,
        onLoadMore: fetchNextPage,
    })

    const handleClickTab = (event: React.SyntheticEvent, newValue: number) => {
        setTab(newValue)
    }

    const isOpenNotificationsSettingsSidebar =
        openSidebarType === SidebarType.NOTIFICATIONS_SETTINGS

    const handleToggleSettings = () => {
        setOpenSidebarType(
            isOpenNotificationsSettingsSidebar ? null : SidebarType.NOTIFICATIONS_SETTINGS,
        )
    }

    const handleReadAllNotifications = () => {
        mutateReadAll({})
    }

    const hasUnreadNotifications = numberOfUnreadNotifications > 0
    const shouldShowReadAllButton = hasUnreadNotifications && tab !== 2

    const tabs = useMemo(
        () => [
            {
                id: '0',
                label: <Typography variant="body2">Все</Typography>,
            },
            {
                id: '1',
                label: (
                    <Stack gap={1} direction="row" alignItems="center">
                        {hasUnreadNotifications && (
                            <Box
                                sx={{
                                    width: '8px',
                                    height: '8px',
                                    backgroundColor: '#EF4848',
                                    borderRadius: '50%',
                                }}
                            />
                        )}
                        <Typography variant="body2">Новые</Typography>
                    </Stack>
                ),
            },
            {
                id: '2',
                label: <Typography variant="body2">Прочитанные</Typography>,
            },
        ],
        [hasUnreadNotifications],
    )

    useEffect(() => {
        if (isSuccessReadAll) {
            invalidateNotificationsListQueries()
            invalidateNotificationsTotalQueries()
        }
    }, [isSuccessReadAll])

    useEffect(() => {
        return () => {
            setOpenSidebarType(null)
        }
    }, [])

    const notificationsList = useMemo(
        () =>
            data?.pages.reduce<UserNotification[]>((acc, page) => acc.concat(page.objects), []) ??
            [],
        [data],
    )

    return (
        <Card
            sx={{
                height: '100%',
                maxHeight: `calc(100dvh - ${isMobile ? APP_TOP_INDENT_MOBILE : APP_TOP_INDENT}px)`,
                p: 0,
                display: 'flex',
                flexDirection: 'column',
            }}>
            <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                sx={{
                    p: 2,
                    pl: 3,
                    borderBottom: '1px solid #F4F4F6',
                }}>
                <Stack direction="row" alignItems="center" gap={2}>
                    <Typography variant="h4">Уведомления</Typography>
                </Stack>
                <Tooltip title="Настройки уведомлений">
                    <IconButton
                        sx={{
                            border: '1px solid #E9EAED',
                            backgroundColor: isOpenNotificationsSettingsSidebar
                                ? '#F4F4F6'
                                : 'transparent',
                        }}
                        size="large"
                        onClick={handleToggleSettings}>
                        <GearIcon />
                    </IconButton>
                </Tooltip>
            </Stack>
            <Stack
                sx={{
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                    padding: isMobile ? '16px 16px 8px 16px' : '16px 16px 8px 24px',
                    gap: 1,
                }}>
                <Tabs
                    value={tab}
                    onChange={handleClickTab}
                    sx={{
                        minHeight: '36px',
                        borderBottom: 'none',
                        p: 0,
                        'button:not(.Mui-selected)': {
                            '.MuiTypography-root': {
                                color: '#5E626E !important',
                            },
                        },
                    }}>
                    {tabs.map(({ label, id }) => (
                        <Tab key={id} id={id} variant="secondary" label={label} />
                    ))}
                </Tabs>
                {!isMobile && shouldShowReadAllButton && (
                    <Button
                        variant="text"
                        sx={{ p: '10px 12px' }}
                        onClick={handleReadAllNotifications}>
                        Прочитать всё
                    </Button>
                )}
            </Stack>
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    p: isMobile ? '8px 16px 0 16px' : '8px  24px 0 24px',
                    height: '100%',
                    flexGrow: 1,
                    flexBasis: 0,
                    overflowY: 'auto',
                }}>
                {isFetching && !isFetchedAfterMount && !isFetchingNextPage ? (
                    <Stack gap={1}>
                        {Array.from({ length: 3 }).map((_, index) => (
                            <Skeleton
                                key={`placeholder-${index}`}
                                variant="rectangular"
                                width="100%"
                                height={100}
                                sx={{ mb: '4px', py: '12px', borderRadius: '16px' }}
                            />
                        ))}
                    </Stack>
                ) : (
                    <>
                        {!notificationsList.length ? (
                            <Stack
                                direction="column"
                                alignItems="center"
                                justifyContent="center"
                                textAlign="center"
                                gap={2}
                                height="100%"
                                pb="52px">
                                <Box
                                    sx={{
                                        width: 'fit-content',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        background: '#F9FAFB',
                                        padding: '16px',
                                        borderRadius: '10px',
                                    }}>
                                    <BellIcon />
                                </Box>
                                <Typography variant="h6" sx={{ textAlign: 'center' }}>
                                    У вас нет уведомлений
                                </Typography>
                            </Stack>
                        ) : (
                            <Stack gap={1} paddingBottom="24px">
                                {notificationsList.map((notification) => (
                                    <NotificationListItem
                                        key={notification.id}
                                        notification={notification}
                                        compact={isMobile}
                                    />
                                ))}
                                {hasNextPage && (
                                    <Skeleton
                                        ref={infiniteRef}
                                        variant="rectangular"
                                        width="100%"
                                        height={100}
                                        sx={{
                                            mb: '4px',
                                            py: '12px',
                                            borderRadius: '16px',
                                            minHeight: '60px',
                                        }}
                                    />
                                )}
                            </Stack>
                        )}
                    </>
                )}
            </Box>
            {isMobile && shouldShowReadAllButton && (
                <>
                    <Divider
                        sx={{
                            borderColor: '#F4F4F6',
                        }}
                    />
                    <Stack
                        sx={{
                            flexDirection: 'row',
                            alignItems: 'center',
                            justifyContent: 'flex-end',
                            padding: '8px 16px 16px 16px',
                            gap: 1,
                        }}>
                        <Button
                            variant="text"
                            sx={{ p: '10px 12px' }}
                            disabled={isPendingReadAll}
                            onClick={handleReadAllNotifications}>
                            Прочитать всё
                        </Button>
                    </Stack>
                </>
            )}
        </Card>
    )
}
